import {type CardData, EntityAction} from "@/domain/type/entity.type.ts";
import {RequestEntityState, RequestEntityStatus, type SourceDetails} from "@/domain/type/synchro.api.type.ts";
import type {RouteLocationAsRelative} from "vue-router";
import {RouteEnum} from "@/router/RouteEnum.ts";
import {useEntitiesStore} from "@/stores/entities.store.ts";
import type {UserDetails} from "@/domain/type/user.type.ts";
import {AbstractEntity} from "@/domain/entity/AbstractEntity.ts";
import type {Source} from "@/domain/type/source.type.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";

export class User extends AbstractEntity {
  cardIcon = 'mdi-account-box-outline'
  left: UserDetails = {
    name: '',
    firstname: '',
    birthDay: '',
    email: '',
    id: '',
    link: ''
  }
  right: UserDetails = {
    name: '',
    firstname: '',
    birthDay: '',
    email: '',
    id: '',
    link: ''
  }

  constructor(
    uuid: string,
    leftFromApi: SourceDetails[],
    rightFromApi: SourceDetails[][],
    state: RequestEntityState,
    status: RequestEntityStatus
  ) {
    super(uuid, leftFromApi, rightFromApi, state, status);
    this.left = this.hydrateDetails(leftFromApi);
    this.right = this.hydrateDetails(rightFromApi[0] ?? []);
    this.leftLink = this.left.link;
    if (this.right.link === '') {
      if (import.meta.env.VITE_AMMON_BASE_URL_OLD === import.meta.env.VITE_AMMON_BASE_URL) {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL_OLD + '/AMMON/COMMUN/Personne/pages/PersonneRecherche.aspx?TypeAffichage=0&AEF=1';
      } else {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL + '/BO/s/COMMUN/Personne/pages/PersonneRecherche.aspx?TypeAffichage=0&AEF=1';
      }
    } else {
      this.rightLink = this.right.link;
    }
  }

  fusionLink(): RouteLocationAsRelative {
    return {
      name: RouteEnum.FusionFromOwner,
      params: {
        id: this.left.id,
        ownerId: useEntitiesStore().ownerId,
        sourceLeft: useEntitiesStore().sourceLeft,
        sourceRight: useEntitiesStore().sourceRight,
        type: SynchroType.PERSONS
      }
    };
  }

  getActions(): EntityAction[] {
    return [
      EntityAction.linkToOwner,
      EntityAction.create,
      EntityAction.tooMany,
      EntityAction.fusion,
      EntityAction.success
    ];
  }

  public getLeftCardData(): CardData[] {
    return this.getCardData(this.left)
  }

  public getRightCardData(): CardData[] {
    return this.getCardData(this.right)
  }

  public getLeftLink(): string {
    return this.left.link;
  }

  public getRightLink(): string {
    return this.right.link;
  }

  match(filter: string): boolean {
    return this.left.name.toLowerCase().includes(filter)
      || this.left.firstname.toLowerCase().includes(filter)
      || this.left.email.toLowerCase().includes(filter)
      || this.left.id.toString().toLowerCase().includes(filter);
  }

  getCreateActionText(srcRight: Source): string {
    const target = srcRight.charAt(0).toUpperCase() + srcRight.slice(1).toLowerCase();
    return 'Êtes-vous certain de vouloir importer le collaborateur dans ' + target + '?';
  }

  protected hydrateDetails(sourceDetails: SourceDetails[]) {
    const details = {
      name: '',
      firstname: '',
      birthDay: '',
      email: '',
      id: '',
      link: ''
    };
    sourceDetails.forEach((detail) => {
      switch (detail.label) {
        case 'Nom':
          details.name = String(detail.val)
          break
        case 'Prénom':
          details.firstname = String(detail.val)
          break
        case 'Date de naissance':
          if (String(detail.val) === '') {
            break;
          }
          details.birthDay = new Date(String(detail.val)).toLocaleDateString()
          break
        case 'Email':
          details.email = String(detail.val)
          break
        case 'Référence EVA':
        case '_id utilisateur':
        case '_id collaborateur':
        case '_id formateur':
        case '_id salarié':
          details.id = String(detail.val)
          break
        case 'Lien':
          details.link = this.filterLink(String(detail.val));
          // console.log(detail.val);
          // details.link = new DOMParser()
          //   .parseFromString(detail.val, 'text/html')
          //   .querySelector('a')?.getAttribute('href') ?? ''
          break
      }
    })

    return details
  }

  private ucFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  private getCardData(data: UserDetails): CardData[] {
    return [
      {
        label: 'Nom',
        value: data.name
      },
      {
        label: 'Prénom',
        value: data.firstname
      },
      {
        label: 'Email',
        value: data.email
      },
      {
        label: 'Date de naissance',
        value: data.birthDay
      },
      {
        label: 'Id',
        value: data.id
      }
    ];
  }
}
